import React from "react";
import { Link } from "react-router-dom";

/**
 * Iter XX.196 — Public account-deletion request page.
 *
 * Public URL: /delete-account   (mounted in App.js — no <Protected> wrap)
 *
 * Purpose: satisfy Google Play's Data Safety / account deletion
 * disclosure requirement with a URL any reviewer or user can reach
 * without signing in. Explains what gets deleted, the two ways to
 * request deletion (in-app self-serve for signed-in users; email for
 * everyone else), the estimated processing timeframe, and the
 * retention posture drawn from the current Privacy policy.
 *
 * IMPORTANT: this page must NOT invent retention rules or timeframes.
 * The email 7-day figure is the operational commitment already stated
 * on /privacy under "Deleting your account" (Iter XX.190).
 */

const CONTACT_EMAIL = "support@gullyapp.com";
const EFFECTIVE_DATE = "8 February 2026";

export default function DeleteAccountRequest() {
  const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    "Delete my Gully account",
  )}&body=${encodeURIComponent(
    "Hi Gully team,\n\n" +
    "Please permanently delete my Gully account and all associated " +
    "personal data.\n\n" +
    "Email on the account: <the email you signed up with>\n" +
    "Reason (optional): \n\n" +
    "Thank you.",
  )}`;

  return (
    <div
      className="grain min-h-screen bg-neutral-950 text-neutral-100"
      data-testid="delete-account-request-page"
    >
      <header
        className="border-b border-neutral-900"
      >
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="font-display text-xl text-neutral-100"
            data-testid="delete-account-home-link"
          >
            gully
          </Link>
          <Link
            to="/privacy"
            className="text-sm text-neutral-400 hover:text-neutral-200"
            data-testid="delete-account-privacy-link"
          >
            Privacy
          </Link>
        </div>
      </header>

      <main
        className="max-w-3xl mx-auto px-6 py-12 sm:py-16"
        data-testid="delete-account-main"
      >
        <p className="label-eyebrow mb-3 text-neutral-500 text-xs uppercase tracking-wider">
          account · delete
        </p>
        <h1
          data-testid="delete-account-title"
          className="font-display font-light text-3xl sm:text-4xl tracking-tight mb-3"
        >
          Delete your Gully account
        </h1>
        <p
          className="text-neutral-500 mb-12 text-sm"
          data-testid="delete-account-effective-date"
        >
          Effective {EFFECTIVE_DATE}.
        </p>

        <div className="space-y-10 text-neutral-300 leading-relaxed">
          <section data-testid="delete-account-intro">
            <p>
              You can request permanent deletion of your Gully account
              and the personal data we&apos;ve stored about you. This page
              is public — you don&apos;t need to be signed in to read it or
              start a request.
            </p>
          </section>

          <section data-testid="delete-account-two-ways">
            <h2 className="font-display text-lg text-neutral-100 mb-3">
              Two ways to request deletion
            </h2>

            <div className="border border-neutral-800 rounded p-5 mb-4">
              <p className="text-sm text-neutral-400 uppercase tracking-wider mb-2">
                Option 1 · Delete it yourself in the app
              </p>
              <p>
                If you can sign in, this is the fastest route.
                It&apos;s a one-click, immediate, permanent action.
              </p>
              <p className="mt-3">
                <Link
                  to="/account/delete"
                  data-testid="delete-account-inapp-link"
                  className="inline-block px-4 py-2 rounded bg-neutral-800 hover:bg-neutral-700 text-sm text-neutral-100"
                >
                  Go to in-app deletion →
                </Link>
              </p>
              <p className="mt-3 text-sm text-neutral-500">
                You&apos;ll be asked to confirm with your password (or a
                confirmation tick if you signed in with Google). After
                you confirm, your account and every piece of data
                listed below is removed immediately.
              </p>
            </div>

            <div className="border border-neutral-800 rounded p-5">
              <p className="text-sm text-neutral-400 uppercase tracking-wider mb-2">
                Option 2 · Email us
              </p>
              <p>
                If you can&apos;t sign in — lost password, changed email,
                account was created by a family member, etc. — email us
                from the address on your Gully account and we&apos;ll
                process the request manually.
              </p>
              <p className="mt-3">
                <a
                  href={mailto}
                  data-testid="delete-account-email-cta"
                  className="inline-block px-4 py-2 rounded bg-neutral-800 hover:bg-neutral-700 text-sm text-neutral-100"
                >
                  Email {CONTACT_EMAIL}
                </a>
              </p>
              <p className="mt-3 text-sm text-neutral-500">
                We aim to complete manual deletion requests within{" "}
                <strong className="text-neutral-300">seven days</strong>{" "}
                of receiving them. This is the same commitment stated
                on our <Link
                  to="/privacy"
                  className="text-[#A7F3D0] underline"
                  data-testid="delete-account-privacy-inline-link"
                >privacy policy</Link>.
              </p>
            </div>
          </section>

          <section data-testid="delete-account-what-removed">
            <h2 className="font-display text-lg text-neutral-100 mb-3">
              What will be deleted
            </h2>
            <p>
              Once your request is processed we permanently remove
              everything linked to your account, including:
            </p>
            <ul className="mt-3 space-y-2 pl-1">
              <li>· Your account, profile name, and any phone number you shared</li>
              <li>· Your Gully login credentials</li>
              <li>· Every conversation you had with Gully</li>
              <li>· Your saved cricket journey and player memory (recurring patterns, notes, arcs)</li>
              <li>· Any videos you uploaded and their AI analyses</li>
              <li>· Any reports, lessons, or coaching summaries Gully wrote for you</li>
              <li>· Your session history, feature-usage counters, and internal event logs tied to your account</li>
            </ul>
          </section>

          <section data-testid="delete-account-retained">
            <h2 className="font-display text-lg text-neutral-100 mb-3">
              What may need to stay
            </h2>
            <p>
              Two narrow items are outside the scope of this deletion
              because we don&apos;t control them from our own systems:
            </p>
            <ul className="mt-3 space-y-2 pl-1">
              <li>
                · <strong className="text-neutral-100">Active Gully Plus subscriptions.</strong>{" "}
                If you have an active paid subscription through Stripe,
                please also cancel it in your Stripe subscription page
                — deleting your Gully account does <em>not</em>{" "}
                automatically cancel the Stripe subscription. Any
                billing records held by Stripe on your behalf are
                retained by Stripe under their own retention policy
                (we don&apos;t store card details ourselves).
              </li>
              <li>
                · <strong className="text-neutral-100">Emails you&apos;ve already received from us.</strong>{" "}
                Any transactional email already delivered to your
                inbox naturally lives on in your own mailbox — we
                can&apos;t reach in and remove those.
              </li>
            </ul>
            <p className="mt-3 text-sm text-neutral-500">
              We don&apos;t currently retain any user-associated data
              inside Gully for fraud-prevention, law-enforcement, or
              audit purposes after deletion. Everything on the list
              above is removed. If our retention posture ever changes,
              this page and the <Link
                to="/privacy"
                className="text-[#A7F3D0] underline"
              >privacy policy</Link> will be updated first.
            </p>
          </section>

          <section data-testid="delete-account-what-happens">
            <h2 className="font-display text-lg text-neutral-100 mb-3">
              What happens next
            </h2>
            <ul className="space-y-2 pl-1">
              <li>
                · <strong className="text-neutral-100">In-app deletion:</strong>{" "}
                immediate. You&apos;re signed out and the account no
                longer exists.
              </li>
              <li>
                · <strong className="text-neutral-100">Email deletion:</strong>{" "}
                we manually process the request within seven days of
                receiving your email. We&apos;ll reply once it&apos;s
                complete.
              </li>
              <li>
                · After deletion, if you sign up again with the same
                email you&apos;ll start a completely fresh Gully account
                — no old conversations or memory carries over.
              </li>
            </ul>
          </section>

          <section data-testid="delete-account-contact">
            <h2 className="font-display text-lg text-neutral-100 mb-3">
              Questions
            </h2>
            <p>
              For anything about this process, deletion status, or
              broader privacy questions, email{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-[#A7F3D0] underline"
                data-testid="delete-account-contact-email"
              >
                {CONTACT_EMAIL}
              </a>
              . The full data-handling policy is on our{" "}
              <Link
                to="/privacy"
                className="text-[#A7F3D0] underline"
              >
                privacy page
              </Link>.
            </p>
          </section>
        </div>

        <div className="mt-16 text-sm text-neutral-500">
          <Link to="/" className="text-[#A7F3D0]">Back to gully</Link>
          {" · "}
          <Link to="/privacy" className="text-[#A7F3D0]">Privacy</Link>
          {" · "}
          <Link to="/terms" className="text-[#A7F3D0]">Terms</Link>
        </div>
      </main>
    </div>
  );
}
